import React, { useState, useEffect } from "react";
import { useSubscription } from "@apollo/client";
import { gql } from "@apollo/client";
import styles from "./style.module.scss";
import {
  WinTAXData,
  ProcessedWinTAXData,
  CarData,
  CombinedData,
} from "../../../types/wintax";

const WIN_TAX_SUBSCRIPTION = gql`
  subscription WinTAXUpdate {
    winTaxUpdate {
      redCar {
        dt
        ADR_Accident_Severity
        CIMState2BMS3
        EERSA1DC_Max
        EERSA1DC_Oft_Cmd
        GPS_AvailSat
        GPS_BestCNO1
        GPS_BestCNO2
        GPS_BestCNO3
        GPS_BestCNO4
        hsc_c_tboard
        ILEDHalo3
        MMAGOffsetManualMax_DRL
        MMAGOffsetManualMax_DRR
        MMAGOffsetManualMin_DRL
        MMAGOffsetManualMin_DRR
        NCodeVersionLEDHalo3
        NCountLEDHalo3
        NFIATeam_alarm
        NFRTeamTelemBMS3p
        NLEDHalo3
        NSerialSparkLogger
        NStatusBBW
        NStatusD2BMS3
        NStatuspBMS3p
        NSWVersionSparkLogger
        Riso1BMS3
        Riso1BMS3p
        SA_CurVol_inbox_headphone_vol
        SA_CurVol_inbox_mic_vol
        SA_CurVol_outbox_headphone_vol
        SA_CurVol_outbox_mic_vol
        SA_DATAMAN_PTP
        SA_GPS_AvailSat
        SA_GPS_BestCNO1
        SA_GPS_BestCNO2
        SA_GPS_BestCNO3
        SA_GPS_BestCNO4
        SA_radioTemp_Corr
        TBoxAct_Corr
        Team_BoostLevelCCSMaxChrgLim
        Team_PERSA4DC
        VRawBackupBattBMS3p
      }
      blueCar {
        dt
        ADR_Accident_Severity
        CIMState2BMS3
        EERSA1DC_Max
        EERSA1DC_Oft_Cmd
        GPS_AvailSat
        GPS_BestCNO1
        GPS_BestCNO2
        GPS_BestCNO3
        GPS_BestCNO4
        hsc_c_tboard
        ILEDHalo3
        MMAGOffsetManualMax_DRL
        MMAGOffsetManualMax_DRR
        MMAGOffsetManualMin_DRL
        MMAGOffsetManualMin_DRR
        NCodeVersionLEDHalo3
        NCountLEDHalo3
        NFIATeam_alarm
        NFRTeamTelemBMS3p
        NLEDHalo3
        NSerialSparkLogger
        NStatusBBW
        NStatusD2BMS3
        NStatuspBMS3p
        NSWVersionSparkLogger
        Riso1BMS3
        Riso1BMS3p
        SA_CurVol_inbox_headphone_vol
        SA_CurVol_inbox_mic_vol
        SA_CurVol_outbox_headphone_vol
        SA_CurVol_outbox_mic_vol
        SA_DATAMAN_PTP
        SA_GPS_AvailSat
        SA_GPS_BestCNO1
        SA_GPS_BestCNO2
        SA_GPS_BestCNO3
        SA_GPS_BestCNO4
        SA_radioTemp_Corr
        TBoxAct_Corr
        Team_BoostLevelCCSMaxChrgLim
        Team_PERSA4DC
        VRawBackupBattBMS3p
      }
    }
  }
`;

const PROCESSED_WIN_TAX_SUBSCRIPTION = gql`
  subscription ProcessedWinTAXUpdate {
    processedWinTAXUpdate {
      redCar {
        dt
        gpsQuality
        sessionEnergyCharged
      }
      blueCar {
        dt
        gpsQuality
        sessionEnergyCharged
      }
    }
  }
`;

function RawWinTAX() {
  const [combinedData, setCombinedData] = useState<CombinedData>({
    redCar: {
      raw: null,
      processed: null,
      lastUpdate: undefined,
    },
    blueCar: {
      raw: null,
      processed: null,
      lastUpdate: undefined,
    },
  });
  const [currentTime, setCurrentTime] = useState(Date.now());

  // Raw WinTAX subscription
  const { loading: rawLoading, error: rawError } = useSubscription(
    WIN_TAX_SUBSCRIPTION,
    {
      shouldResubscribe: true,
      onSubscriptionData: ({ subscriptionData }) => {
        const update = subscriptionData?.data?.winTaxUpdate;
        if (!update) return;

        setCombinedData((prevData) => ({
          redCar: {
            ...prevData.redCar,
            raw: update.redCar || prevData.redCar.raw,
            lastUpdate: update.redCar ? Date.now() : prevData.redCar.lastUpdate,
          },
          blueCar: {
            ...prevData.blueCar,
            raw: update.blueCar || prevData.blueCar.raw,
            lastUpdate: update.blueCar
              ? Date.now()
              : prevData.blueCar.lastUpdate,
          },
        }));
      },
    },
  );

  // Processed WinTAX subscription
  const { loading: processedLoading, error: processedError } = useSubscription(
    PROCESSED_WIN_TAX_SUBSCRIPTION,
    {
      shouldResubscribe: true,
      onSubscriptionData: ({ subscriptionData }) => {
        const update = subscriptionData?.data?.processedWinTAXUpdate;
        if (!update) return;

        setCombinedData((prevData) => ({
          redCar: {
            ...prevData.redCar,
            processed: update.redCar || prevData.redCar.processed,
            lastUpdate: update.redCar ? Date.now() : prevData.redCar.lastUpdate,
          },
          blueCar: {
            ...prevData.blueCar,
            processed: update.blueCar || prevData.blueCar.processed,
            lastUpdate: update.blueCar
              ? Date.now()
              : prevData.blueCar.lastUpdate,
          },
        }));
      },
    },
  );

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const isDataFresh = (lastUpdate?: number) => {
    if (!lastUpdate) return false;
    return currentTime - lastUpdate < 5000; // 5 seconds threshold
  };

  const renderCarData = (carData: CarData, title: string) => {
    if (!carData) return null;

    return (
      <div className={styles.carSection}>
        <div className={styles.titleRow}>
          <h2
            className={styles.carTitle}
            data-car={title.toLowerCase().split(" ")[0]}
          >
            {title}
          </h2>
          <div
            className={`${styles.statusIndicator} ${
              isDataFresh(carData.lastUpdate) ? styles.active : styles.inactive
            }`}
          />
        </div>
        <div className={styles.dataGridContainer}>
          {renderDataGrid(carData.raw, "raw")}
          {renderDataGrid(carData.processed, "processed")}
        </div>
      </div>
    );
  };

  const renderDataGrid = (
    data: WinTAXData | ProcessedWinTAXData | null,
    type: "raw" | "processed",
  ) => {
    if (!data) return null;

    const entries = Object.entries(data);
    return (
      <div className={styles.dataGrid}>
        <h3 className={styles.dataType}>{type.toUpperCase()}</h3>
        {entries.map(([key, value]) => (
          <div
            key={key}
            className={`${styles.gridItem} ${
              key === "dt" ? styles.timestamp : ""
            }`}
          >
            <span className={styles.label}>{key}</span>
            <span className={styles.value}>
              {key === "dt" ? (
                <div className={styles.timestampWrapper}>
                  <div>
                    UTC:{" "}
                    {new Date(value as number).toLocaleTimeString("en-GB", {
                      timeZone: "UTC",
                    })}
                  </div>
                  <div>
                    Local: {new Date(value as number).toLocaleTimeString()}
                  </div>
                </div>
              ) : typeof value === "number" ? (
                value.toFixed(2)
              ) : (
                value?.toString() || "N/A"
              )}
            </span>
          </div>
        ))}
      </div>
    );
  };

  if (rawLoading && processedLoading)
    return <div className={styles.loading}>Connecting to WinTAX data...</div>;

  if (rawError || processedError)
    return (
      <div className={styles.error}>
        Error: {rawError?.message || processedError?.message}
      </div>
    );

  return (
    <div className={styles.container}>
      <div className={styles.carsContainer}>
        {renderCarData(combinedData.blueCar, "Blue Car")}
        {renderCarData(combinedData.redCar, "Red Car")}
      </div>
    </div>
  );
}

export default RawWinTAX;
